import { useState, useCallback } from 'react';
import { Link as MuiLink } from '@mui/material';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import {
  getEmployeeAttendance,
  getEmployeeAttendanceDetails,
  createEmployeeAttendance,
  updateEmployeeAttendance,
  deleteEmployeeAttendance,
  batchCreateAttendance,
  uploadAttendanceFile,
} from '../services/api';

export const useAttendanceOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState({
    total_records: 0,
    present_count: 0,
    absent_count: 0,
    on_leave_count: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});

  const loadAttendance = useCallback(
    async (page, rowsPerPage, searchTerm, employeeFilter, departmentFilter, statusFilter, startDate, endDate) => {
      await withLoading(async () => {
        try {
          const employeeParam = employeeFilter === 'All' ? '' : employeeFilter;
          const departmentParam = departmentFilter === 'All' ? '' : departmentFilter;
          const statusParam = statusFilter === 'All' ? '' : statusFilter;
          
          const response = await getEmployeeAttendance(
            page + 1, // API expects 1-based pagination
            rowsPerPage,
            searchTerm,
            employeeParam,
            departmentParam,
            statusParam,
            startDate,
            endDate
          );

          if (response.data) {
            setAttendance(response.data.results || []);
            setTotalCount(response.data.pagination_info?.count || 0);

            // Update stats if included in response
            if (response.data.stats) {
              setStats(response.data.stats);
            }

            // Update filter options if included in response
            if (response.data.filter_options) {
              const { filter_options: responseFilters } = response.data;
              setFilterOptions(responseFilters);
            }
          }
        } catch (error) {
          console.error('Error loading attendance:', error);
          toast.error('Failed to load attendance records');
        }
      });
    },
    [withLoading]
  );

  const createAttendanceRecord = useCallback(async (attendanceData) => {
    try {
      await createEmployeeAttendance(attendanceData);
      toast.success('Attendance record created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating attendance:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create attendance record');
      }
      return false;
    }
  }, []);

  const updateAttendanceRecord = useCallback(async (attendanceId, attendanceData) => {
    try {
      await updateEmployeeAttendance(attendanceId, attendanceData);
      toast.success('Attendance record updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating attendance:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update attendance record');
      }
      return false;
    }
  }, []);

  const deleteAttendanceRecord = useCallback(async (attendanceId) => {
    try {
      await deleteEmployeeAttendance(attendanceId);
      toast.success('Attendance record deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting attendance:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to delete attendance record');
      }
      return false;
    }
  }, []);

  const getAttendanceDetails = useCallback(async (attendanceId) => {
    try {
      const response = await getEmployeeAttendanceDetails(attendanceId);
      return response.data;
    } catch (error) {
      console.error('Error getting attendance details:', error);
      toast.error('Failed to load attendance details');
      return null;
    }
  }, []);

  const batchCreateAttendanceRecords = useCallback(async (attendanceRecords) => {
    try {
      const response = await batchCreateAttendance(attendanceRecords);
      
      if (response.data) {
        const { status, created_records: createdRecords, skipped_records: skippedRecords, errors, message } = response.data;
        
        if (status === 'success' || status === 'partial_success') {
          toast.success(message || `Successfully created ${createdRecords.length} attendance records`);
          
          if (skippedRecords && skippedRecords.length > 0) {
            console.log('Skipped records:', skippedRecords);
            toast.info(`${skippedRecords.length} employees already have attendance for today`);
          }
          
          if (errors && errors.length > 0) {
            console.warn('Batch attendance errors:', errors);
            errors.forEach(error => toast.warning(error));
          }
          
          return true;
        }
        
        toast.error(message || 'Failed to create batch attendance');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error creating batch attendance:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create batch attendance');
      }
      return false;
    }
  }, []);

  const uploadAttendanceCsvFile = useCallback(async ({ file, shiftId, attendanceDate, markMissingAsAbsent }) => {
    try {
      const data = await uploadAttendanceFile(file, shiftId, attendanceDate, markMissingAsAbsent);

      if (data) {
        const {
          status,
          created_records: createdRecords = [],
          updated_records: updatedRecords = [],
          errors = [],
          background_task_id: backgroundTaskId,
          message,
        } = data;

        if (status === 'success' || status === 'partial_success') {
          if (backgroundTaskId) {
            const successText = message || 'Attendance CSV queued for background processing.';
            const successMessage = (
              <>
                {`${successText} Background task ID: ${backgroundTaskId}.`}
                <br />
                <MuiLink href="/background-tasks" underline="always" sx={{ fontWeight: 700 }}>
                  View Tasks
                </MuiLink>
              </>
            );
            toast.success(successMessage);
          } else {
            toast.success(
              message || `Attendance CSV processed. Created: ${createdRecords.length}, Updated: ${updatedRecords.length}`
            );
          }

          if (errors.length > 0) {
            toast.warning(`${errors.length} row(s) could not be processed.`);
            console.warn('Attendance upload errors:', errors);
          }

          return backgroundTaskId || true;
        }

        toast.error(message || 'Failed to upload attendance CSV');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error uploading attendance CSV:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to upload attendance CSV');
      }
      return false;
    }
  }, []);

  // Extract employees from filterOptions for compatibility
  const employees = filterOptions.employees || [];
  const activeEmployees = employees.filter((emp) => emp.is_active);

  return {
    isLoading,
    attendance,
    stats,
    totalCount,
    employees,
    activeEmployees,
    filterOptions,
    loadAttendance,
    createAttendanceRecord,
    updateAttendanceRecord,
    deleteAttendanceRecord,
    getAttendanceDetails,
    batchCreateAttendanceRecords,
    uploadAttendanceCsvFile,
  };
};